import type { ScannerRuntimeAction, ScannerRuntimeMode } from '@planuze/admin-contracts';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  InlineAlert,
  Spinner,
  StatusBadge,
} from '@planuze/ui';
import { RefreshCw } from 'lucide-react';
import { useScannerRuntimeContent } from '../../content/sections/scanner-runtime.js';
import { useScannerRuntimeStatus } from '../../hooks/use-moderation-config.js';

function modeLabel(
  mode: ScannerRuntimeMode,
  copy: ReturnType<typeof useScannerRuntimeContent>,
): string {
  switch (mode) {
    case 'oidc':
      return copy.mode.oidc;
    case 'legacy_runner':
      return copy.mode.legacyRunner;
    case 'unavailable':
      return copy.mode.unavailable;
  }
}

function actionLabel(
  action: ScannerRuntimeAction,
  copy: ReturnType<typeof useScannerRuntimeContent>,
): string {
  switch (action) {
    case 'none':
      return copy.action.none;
    case 'configure_registry':
      return copy.action.configureRegistry;
    case 'configure_runner':
      return copy.action.configureRunner;
    case 'check_runner':
      return copy.action.checkRunner;
    case 'check_registry':
      return copy.action.checkRegistry;
  }
}

export function ScannerRuntimeStatusCard({ policyEnabled }: { readonly policyEnabled?: boolean }) {
  const copy = useScannerRuntimeContent();
  const runtime = useScannerRuntimeStatus();
  const status = runtime.data;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="space-y-1.5">
          <CardTitle>{copy.title}</CardTitle>
          <CardDescription>{copy.description}</CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          loading={runtime.isFetching}
          onClick={() => runtime.refetch()}
        >
          <RefreshCw className="size-4" />
          {copy.refresh}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {runtime.isLoading || status === undefined ? (
          runtime.isError ? (
            <InlineAlert variant="destructive" title={copy.loadErrorTitle}>
              {copy.loadErrorDescription}
            </InlineAlert>
          ) : (
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <Spinner size="sm" />
              {copy.loading}
            </div>
          )
        ) : (
          <>
            <div className="grid gap-3 lg:grid-cols-3">
              <div className="rounded-md border border-border p-3">
                <p className="text-xs text-muted-foreground">{copy.modeLabel}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <StatusBadge variant="info">{modeLabel(status.mode, copy)}</StatusBadge>
                  <StatusBadge variant={status.ready ? 'success' : 'destructive'} dot>
                    {status.ready ? copy.ready : copy.unavailable}
                  </StatusBadge>
                </div>
              </div>
              <div className="rounded-md border border-border p-3">
                <p className="text-xs text-muted-foreground">{copy.legacyRunnerLabel}</p>
                <div className="mt-2 space-y-2 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">{copy.adminServiceLabel}</span>
                    <StatusBadge
                      variant={status.legacyRunner.adminConfigured ? 'success' : 'warning'}
                    >
                      {status.legacyRunner.adminConfigured ? copy.configured : copy.missing}
                    </StatusBadge>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">{copy.packRegistryLabel}</span>
                    <StatusBadge
                      variant={status.legacyRunner.registryConfigured ? 'success' : 'warning'}
                    >
                      {status.legacyRunner.registryConfigured ? copy.configured : copy.missing}
                    </StatusBadge>
                  </div>
                  <p className="font-medium text-foreground">
                    {!status.legacyRunner.required
                      ? copy.legacyNotRequired
                      : !status.legacyRunner.adminConfigured ||
                          !status.legacyRunner.registryConfigured
                        ? copy.legacyNotConfigured
                        : status.legacyRunner.reachable === true
                          ? copy.legacyReady
                          : copy.legacyUnreachable}
                  </p>
                </div>
              </div>
              <div className="rounded-md border border-border p-3">
                <p className="text-xs text-muted-foreground">{copy.centralScanLabel}</p>
                <div className="mt-2 space-y-2 text-sm">
                  <StatusBadge
                    variant={
                      !status.centralScan.checked
                        ? 'warning'
                        : status.centralScan.ready
                          ? 'success'
                          : 'warning'
                    }
                    dot
                  >
                    {!status.centralScan.checked
                      ? copy.centralNotChecked
                      : status.centralScan.ready
                        ? copy.centralReady
                        : copy.centralUnavailable}
                  </StatusBadge>
                  {status.centralScan.checked && status.centralScan.ready ? (
                    <p className="font-medium text-foreground">{copy.centralReadyDescription}</p>
                  ) : status.centralScan.missing.length > 0 ? (
                    <div className="space-y-1.5">
                      <p className="font-medium text-foreground">
                        {copy.centralMissingDescription}
                      </p>
                      <ul className="space-y-1 text-xs text-muted-foreground">
                        {status.centralScan.missing.map((binding) => (
                          <li key={binding}>
                            <code>{binding}</code>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {status.mode === 'oidc' && status.ready ? (
              <InlineAlert variant="success" title={copy.oidcReadyTitle}>
                {copy.oidcReadyDescription}
              </InlineAlert>
            ) : status.action !== 'none' ? (
              <InlineAlert
                variant={
                  status.action === 'configure_registry' || status.action === 'configure_runner'
                    ? 'warning'
                    : 'destructive'
                }
              >
                {actionLabel(status.action, copy)}
              </InlineAlert>
            ) : null}

            {policyEnabled === false && (
              <InlineAlert variant="warning">{copy.policyDisabled}</InlineAlert>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
